'use client'

import React from 'react'
import classNames from 'classnames'
import { CMSImage } from '@/ui/atoms/CMSImage'
import { Media } from '@/payload-types'

export type QuoteProps = {
  text: string
  authorName: string
  authorTitle?: string
  authorImage?: Media | null
  authorLink?: string
  className?: string
  variant?: 'default' | 'compact'
  /** When true, the surrounding “” quotation marks are not rendered. Defaults to false. */
  hideQuoteMarks?: boolean
}

export const Quote: React.FC<QuoteProps> = ({
  text,
  authorName,
  authorTitle,
  authorImage,
  authorLink,
  className,
  variant = 'default',
  hideQuoteMarks = false,
}) => {
  const isCompact = variant === 'compact'

  const authorContent = (
    <>
      {authorImage && (
        <div className="flex-shrink-0 mb-3">
          <div
            className={classNames(
              'relative rounded-full overflow-hidden',
              isCompact ? 'w-12 h-12 md:w-14 md:h-14' : 'w-14 h-14 md:w-16 md:h-16',
            )}
          >
            <CMSImage
              resource={authorImage}
              className="w-full h-full object-cover"
              width={isCompact ? 56 : 64}
              height={isCompact ? 56 : 64}
            />
          </div>
        </div>
      )}

      {/* Author Name and Title */}
      <cite className="not-italic">
        <p
          className={classNames(
            'text-text-base',
            isCompact ? 'text-body-XS-medium' : 'text-body-S-medium',
            authorLink && 'hover:text-blue-20 transition-colors',
          )}
        >
          {authorName}
          {authorTitle && (
            <span
              className={classNames(
                'text-text-subtle',
                isCompact ? 'text-body-XS-regular' : 'text-body-S-regular',
              )}
            >
              {', '}
              {authorTitle}
            </span>
          )}
        </p>
      </cite>
    </>
  )

  return (
    <div
      className={classNames(
        'bg-surface-subtle rounded-lg',
        isCompact ? 'm-4 lg:m-6 p-6 lg:p-8' : 'm-6 lg:m-8 p-8 lg:p-12',
        className,
      )}
    >
      <div className="flex flex-col items-center text-center">
        {/* Quote Content */}
        <blockquote className="mb-6 lg:mb-8">
          <p
            className={classNames(
              'italic text-text-subtle leading-relaxed',
              isCompact
                ? 'text-body-M-regular md:text-body-L-regular'
                : 'text-body-L-regular md:text-title-M-regular',
            )}
          >
            {hideQuoteMarks ? text : <>&ldquo;{text}&rdquo;</>}
          </p>
        </blockquote>

        {/* Author Image and Info */}
        {authorLink ? (
          <a
            href={authorLink}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col items-center"
          >
            {authorContent}
          </a>
        ) : (
          <div className="flex flex-col items-center">{authorContent}</div>
        )}
      </div>
    </div>
  )
}
